import {useMemo} from 'react';

import {Box, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';

import {getInitials} from '@/utils/getInitials';
import {parseNumberToCurrency} from '@/utils/parsingCurrency';

interface Participant {
  name: string;
  split_amount: number;
  owner?: boolean;
}

interface SplitsConfirmationContentProps {
  inviterName: string;
  totalAmountPaid: number;
  description?: string;
  participants?: Participant[];
}

interface ParticipantRowProps {
  participant: Participant;
}

interface InfoRowProps {
  label: string;
  value: string | number;
  className?: string;
  icon?: React.ReactNode;
}

const InfoRow = ({label, value, className = '', icon}: InfoRowProps) => (
  <Box className={`splits-confirmation-row ${className}`}>
    <Box className="splits-confirmation-row-left">
      {icon}
      <Typography
        component="span"
        className="splits-confirmation-label">
        {label}
      </Typography>
    </Box>
    <Typography
      component="span"
      className="splits-confirmation-value">
      {value}
    </Typography>
  </Box>
);

const ParticipantRow = ({participant}: ParticipantRowProps) => {
  const {t} = useTranslation();
  return (
    <InfoRow
      className="splits-confirmation-row-owes"
      icon={
        <span className="splits-confirmation-initial">
          {getInitials(participant.name)}
        </span>
      }
      label={t('split_bills_page.confirmation.owes', {name: participant.name})}
      value={parseNumberToCurrency(participant.split_amount)}
    />
  );
};

const SplitsConfirmationContentData = ({
  inviterName,
  totalAmountPaid,
  description,
  participants,
}: SplitsConfirmationContentProps) => {
  const {t} = useTranslation();

  const participantsToShow = useMemo(() => {
    return (participants ?? []).filter(Boolean).filter(p => !p.owner);
  }, [participants]);

  return (
    <>
      <InfoRow
        label={t('split_bills_page.confirmation.paid', {name: inviterName})}
        value={parseNumberToCurrency(totalAmountPaid)}
      />

      {!!description && (
        <InfoRow
          label={t('split_bills_page.confirmation.description')}
          value={description}
        />
      )}

      <div className="splits-confirmation-divider" />

      {participantsToShow.map(p => (
        <ParticipantRow
          key={`${p.name}-${p.split_amount}`}
          participant={p}
        />
      ))}
    </>
  );
};

export default SplitsConfirmationContentData;
